import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { makeRequest } from '@ink-ai/portal/common/requestHelper';
import { handleDownload } from '@ink-ai/portal/reducers/translation';
import { app } from '@ink-ai/portal/reducers/app';

/**
 * Text-only editor for an XML translation (define.xml).
 *
 * Only the `TranslatedText` nodes the SUBMISSION service translates are shown
 * (see TRANSLATED_OWNERS), one row each, paired by document order with the same
 * nodes of the source file: translation changes text and
 * nothing else, so the n-th node of one file is the n-th node of the other. Tags,
 * attributes and code values (CheckValue, OID…) never reach the editor, so they
 * cannot be broken. Saving writes the edited text back into the translated
 * document and uploads the whole file; the server validates it before it
 * replaces the translation, which download and the external API then serve.
 */

type Segment = {
  index: number;
  /** Element that owns the text, e.g. ItemDef / CommentDef / MethodDef. */
  owner: string;
  oid: string;
  name: string;
  source: string;
};

const TEXT_ELEMENT = 'TranslatedText';
const PAGE_SIZE_OPTIONS = [25, 50, 100];

/**
 * Only the text the SUBMISSION service translates is offered for editing: its XML
 * translator defaults to `def:CommentDef/Description/TranslatedText` and
 * `MethodDef/Description/TranslatedText` (`sync_xml_translator.py`), and leaves
 * everything else in English on purpose. Keep in step with that list.
 */
const TRANSLATED_OWNERS = ['CommentDef', 'MethodDef'];
const isTranslatedBySubmission = (node: Element): boolean => {
  const description = node.parentElement;
  return (
    description?.localName === 'Description' &&
    TRANSLATED_OWNERS.includes(description.parentElement?.localName || '')
  );
};

const parseXml = (text: string): Document => {
  const doc = new DOMParser().parseFromString(text.replace(/^\uFEFF/, ''), 'application/xml');
  if (doc.getElementsByTagName('parsererror').length > 0) {
    throw new Error('parse');
  }
  return doc;
};

/** Nearest ancestor carrying an OID: that is what the text describes. */
const ownerOf = (node: Element): { owner: string; oid: string; name: string } => {
  let el: Element | null = node.parentElement;
  while (el) {
    const oid = el.getAttribute('OID');
    if (oid) {
      return { owner: el.localName, oid, name: el.getAttribute('Name') || '' };
    }
    el = el.parentElement;
  }
  return { owner: node.parentElement?.localName || '', oid: '', name: '' };
};

const fetchText = async (id: string, which: 'original' | 'translated'): Promise<string> => {
  const json = await makeRequest<{ url: string }>({
    method: 'GET',
    url: `/api/v1/document/translation/${id}/preview/${which}`,
    responseType: 'json',
  });
  const resp = await axios.get<ArrayBuffer>(json.url, { responseType: 'arraybuffer' });
  return new TextDecoder('utf-8').decode(resp.data);
};

const highlight = (text: string, query: string): React.ReactNode => {
  if (!query) return text;
  const lower = text.toLowerCase();
  const q = query.toLowerCase();
  const parts: React.ReactNode[] = [];
  let from = 0;
  let at = lower.indexOf(q);
  while (at >= 0) {
    parts.push(text.slice(from, at), <mark key={at}>{text.slice(at, at + q.length)}</mark>);
    from = at + q.length;
    at = lower.indexOf(q, from);
  }
  parts.push(text.slice(from));
  return parts;
};

export const XmlTranslationEditor = () => {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [taskName, setTaskName] = useState('');
  const [translatedDoc, setTranslatedDoc] = useState<Document | null>(null);
  const [declaration, setDeclaration] = useState('');
  const [segments, setSegments] = useState<Segment[]>([]);
  /** Translation as last saved, by segment index. */
  const [saved, setSaved] = useState<string[]>([]);
  /** Unsaved edits, by segment index. */
  const [edits, setEdits] = useState<Record<number, string>>({});
  const [saving, setSaving] = useState(false);

  const [ownerFilter, setOwnerFilter] = useState('');
  const [query, setQuery] = useState('');
  const [linkDuplicates, setLinkDuplicates] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const detail = await makeRequest<{ name?: string }>({
        method: 'GET',
        url: `/api/v1/document/translation/${id}`,
        responseType: 'json',
      });
      setTaskName(detail?.name || '');
      const [sourceText, translatedText] = await Promise.all([
        fetchText(id, 'original'),
        fetchText(id, 'translated'),
      ]);
      const sourceDoc = parseXml(sourceText);
      const trDoc = parseXml(translatedText);
      const sourceNodes = Array.from(sourceDoc.getElementsByTagNameNS('*', TEXT_ELEMENT));
      const trNodes = Array.from(trDoc.getElementsByTagNameNS('*', TEXT_ELEMENT));
      if (sourceNodes.length !== trNodes.length) {
        setLoadError(t('translation.xmlEditor.structureMismatch'));
        return;
      }
      // Kept verbatim and written back on save (see save() for why it is added by hand).
      const decl = translatedText.replace(/^\uFEFF/, '').match(/^\s*<\?xml[^?]*\?>/);
      setDeclaration(decl ? decl[0].trim() : '<?xml version="1.0" encoding="UTF-8"?>');
      setTranslatedDoc(trDoc);
      // Pair every TranslatedText by position, then keep the ones SUBMISSION
      // translates. `index` stays the position among all of them, which is what
      // save() uses to find the node again.
      setSegments(
        sourceNodes.flatMap((node, index) =>
          isTranslatedBySubmission(node)
            ? [{ index, ...ownerOf(node), source: node.textContent || '' }]
            : [],
        ),
      );
      setSaved(trNodes.map((node) => node.textContent || ''));
      setEdits({});
    } catch (e) {
      console.error(e);
      setLoadError(t('translation.xmlEditor.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    load();
  }, [load]);

  const dirty = Object.keys(edits).length > 0;
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const valueOf = (index: number) => (index in edits ? edits[index] : saved[index]);

  const indicesBySource = useMemo(() => {
    const map = new Map<string, number[]>();
    segments.forEach((s) => {
      const key = s.source.trim();
      map.set(key, [...(map.get(key) || []), s.index]);
    });
    return map;
  }, [segments]);

  const owners = useMemo(
    () => Array.from(new Set(segments.map((s) => s.owner))).sort(),
    [segments],
  );

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return segments.filter((s) => {
      if (ownerFilter && s.owner !== ownerFilter) return false;
      const value = valueOf(s.index);
      if (q && !s.source.toLowerCase().includes(q) && !value.toLowerCase().includes(q) && !s.oid.toLowerCase().includes(q)) {
        return false;
      }
      return true;
    });
    // valueOf reads saved/edits, which are listed below.
  }, [segments, ownerFilter, query, edits, saved]);

  useEffect(() => {
    setPage(0);
  }, [ownerFilter, query]);

  const editSegment = (segment: Segment, value: string) => {
    const targets = linkDuplicates
      ? indicesBySource.get(segment.source.trim()) || [segment.index]
      : [segment.index];
    setEdits((prev) => {
      const next = { ...prev };
      targets.forEach((i) => {
        if (value === saved[i]) delete next[i];
        else next[i] = value;
      });
      return next;
    });
  };

  const save = async () => {
    if (!translatedDoc || !dirty) return;
    setSaving(true);
    try {
      const nodes = Array.from(translatedDoc.getElementsByTagNameNS('*', TEXT_ELEMENT));
      // Setting textContent escapes `<` and `&`, so edited text cannot break the markup.
      Object.entries(edits).forEach(([i, value]) => {
        nodes[Number(i)].textContent = value;
      });
      // Serialize the top-level nodes one by one and add the declaration ourselves.
      // Serializing the whole Document is not portable: Chrome re-emits the XML
      // declaration (so prepending one makes the file invalid) while other engines
      // may not. Per-node output never includes it, and joining with newlines also
      // keeps the stylesheet PI and header comments on their own lines.
      const serializer = new XMLSerializer();
      const xml = [declaration, ...Array.from(translatedDoc.childNodes).map((n) => serializer.serializeToString(n))]
        .join('\n');
      const { uploadUrl } = await makeRequest<{ uploadUrl: string }>({
        method: 'POST',
        url: `/api/v1/document/translation/${id}/xml-edit/upload-url`,
      });
      await axios.put(uploadUrl, new Blob([xml], { type: 'application/xml' }));
      await makeRequest({ method: 'POST', url: `/api/v1/document/translation/${id}/xml-edit/commit` });
      setSaved((prev) => prev.map((v, i) => (i in edits ? edits[i] : v)));
      setEdits({});
      dispatch(app.actions.setGlobalMessage({ message: t('translation.xmlEditor.saved'), status: 'success' }));
    } catch (e: any) {
      console.error(e);
      // The DOM may hold edits the server rejected; reload so it matches the stored file.
      const detail = e?.response?.data?.message;
      dispatch(app.actions.setGlobalMessage({
        message: `${t('translation.xmlEditor.saveFailed')}${detail ? `: ${detail}` : ''}`,
        status: 'error',
      }));
      await load();
    } finally {
      setSaving(false);
    }
  };

  const pageRows = visible.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Container maxWidth={false} sx={{ py: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
          {t('common.back')}
        </Button>
        <Typography variant="h6" sx={{ flexGrow: 1 }} noWrap>
          {t('translation.xmlEditor.title')}{taskName ? ` · ${taskName}` : ''}
        </Typography>
        <Button
          variant="outlined"
          disabled={dirty || loading}
          onClick={() => handleDownload(id, undefined, 'collabora')}
        >
          {t('translation.xmlEditor.download')}
        </Button>
        <LoadingButton variant="contained" loading={saving} disabled={!dirty} onClick={save}>
          {t('translation.xmlEditor.save', { count: Object.keys(edits).length })}
        </LoadingButton>
      </Stack>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}
      {!loading && loadError && <Alert severity="error">{loadError}</Alert>}
      {!loading && !loadError && segments.length === 0 && (
        <Alert severity="info">{t('translation.xmlEditor.noText')}</Alert>
      )}

      {!loading && !loadError && segments.length > 0 && (
        <>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap' }}>
            <TextField
              select
              size="small"
              label={t('translation.xmlEditor.element')}
              value={ownerFilter}
              onChange={(e) => setOwnerFilter(e.target.value)}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="">{t('translation.xmlEditor.allElements')}</MenuItem>
              {owners.map((o) => (
                <MenuItem key={o} value={o}>{o}</MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label={t('common.search')}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              sx={{ minWidth: 260 }}
            />
            {/* Locked while there are unsaved edits, so one save never mixes linked and unlinked edits. */}
            <Tooltip title={dirty ? t('translation.xmlEditor.linkDuplicatesLocked') : ''}>
              {/* span: a disabled control fires no events for the Tooltip to catch. */}
              <span>
                <FormControlLabel
                  disabled={dirty}
                  control={<Checkbox checked={linkDuplicates} onChange={(e) => setLinkDuplicates(e.target.checked)} />}
                  label={t('translation.xmlEditor.linkDuplicates')}
                />
              </span>
            </Tooltip>
          </Stack>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '18%' }}>{t('translation.xmlEditor.location')}</TableCell>
                  <TableCell sx={{ width: '41%' }}>{t('translation.xmlEditor.source')}</TableCell>
                  <TableCell sx={{ width: '41%' }}>{t('translation.xmlEditor.translation')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pageRows.map((s) => {
                  const value = valueOf(s.index);
                  const untranslated = value.trim() === s.source.trim();
                  const modified = s.index in edits;
                  const duplicates = (indicesBySource.get(s.source.trim()) || []).length;
                  return (
                    <TableRow
                      key={s.index}
                      sx={{
                        // Amber: still identical to the source. Left bar: edited, not yet saved.
                        backgroundColor: untranslated ? 'rgba(255, 193, 7, 0.12)' : undefined,
                        borderLeft: modified ? 3 : 0,
                        borderLeftColor: 'primary.main',
                      }}
                    >
                      <TableCell sx={{ verticalAlign: 'top' }}>
                        <Typography variant="body2" fontWeight={500}>{s.owner}</Typography>
                        <Typography variant="caption" color="text.secondary" component="div">
                          {highlight(s.oid, query.trim())}
                        </Typography>
                        {s.name && (
                          <Typography variant="caption" color="text.secondary" component="div">{s.name}</Typography>
                        )}
                        {duplicates > 1 && (
                          <Typography variant="caption" color="primary" component="div">
                            ×{duplicates}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ verticalAlign: 'top', whiteSpace: 'pre-wrap' }}>
                        {highlight(s.source, query.trim())}
                      </TableCell>
                      <TableCell sx={{ verticalAlign: 'top' }}>
                        <TextField
                          fullWidth
                          multiline
                          size="small"
                          value={value}
                          onChange={(e) => editSegment(s, e.target.value)}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={visible.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={PAGE_SIZE_OPTIONS}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setPage(0);
            }}
          />
        </>
      )}
    </Container>
  );
};
